import * as XLSX from 'xlsx';
import {
  BatchDiagnostic,
  CarrierUnitDto,
  KnownBatchDiagnosticSeverity
} from '@groundfloor-platform/mgmt-service-g-sdk';
import {
  CourierBatchField,
  CourierBatchValues,
  CourierBatchRow,
  CourierBatchResolvedCarrier
} from './courier-batch.map';

export type CourierBatchDiagnosticSeverity = 'error' | 'warning';

export interface ParsedCourierCsv {
  columns: string[];
  rows: string[][];
}

export type CourierCsvParseErrorReason = 'NO_HEADINGS' | 'NO_ROWS';

export class CourierCsvParseError extends Error {
  // A declared field, not a parameter property: Storybook's Babel can't parse those.
  public readonly reason: CourierCsvParseErrorReason;

  constructor(reason: CourierCsvParseErrorReason) {
    super(
      reason === 'NO_HEADINGS'
        ? 'The CSV data has no heading row. Add the headings from the template and try again.'
        : 'The CSV data has no couriers beneath the heading row. Add at least one courier and try again.'
    );
    this.name = 'CourierCsvParseError';
    this.reason = reason;
  }
}

export function parseCourierCsv(text: string): ParsedCourierCsv {
  const workbook = XLSX.read(text.replace(/^﻿/, ''), { type: 'string', raw: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const table = sheet ? XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '' }) : [];

  const [headings, ...rows] = table
    .map((cells) => cells.map((cell) => (cell === undefined || cell === null ? '' : String(cell))))
    .filter((cells) => cells.some((cell) => cell.trim() !== ''));

  if (!headings) {
    throw new CourierCsvParseError('NO_HEADINGS');
  }

  if (rows.length === 0) {
    throw new CourierCsvParseError('NO_ROWS');
  }

  return { columns: headings.map((heading) => heading.trim()), rows };
}

/** Stable across every prepare and import, so diagnostics and results can find their row. */
export function makeRowIDs(count: number): string[] {
  return Array.from({ length: count }, (_, index) => `row-${index + 1}`);
}

/**
 * A row's number, from its ID: `row-5` is 5. It's the number the grid shows, and the one each row is
 * given as its source row, so the API's messages name rows as the grid does.
 */
export function rowNumberOf(rowID: string): number | undefined {
  const match = /^row-(\d+)$/.exec(rowID);
  return match ? Number(match[1]) : undefined;
}

/** The codes the API gives a batch's diagnostics. */
export const CourierBatchDiagnosticCode = {
  ColumnsUnmapped: 'columns.unmapped',
  NotFound: 'courier.notFound',
  Ambiguous: 'courier.ambiguous',
  DuplicateRow: 'courier.duplicateRow',
  DuplicateValue: 'courier.duplicateValue',
  MatchedByCredential: 'courier.matchedByCredential',
  NoSignIn: 'courier.noSignIn',
  AwaitingReview: 'courier.awaitingReview',
  CarrierMissing: 'carrier.missing',
  CarrierForbidden: 'carrier.forbidden',
  CarrierUnavailable: 'carrier.unavailable',
  CarrierUnknown: 'carrier.unknown',
  CarrierAmbiguous: 'carrier.ambiguous',
  WrongCarrier: 'courier.wrongCarrier'
} as const;

/** The text cells a code points at, whatever field its diagnostic names. */
const CELLS_BY_CODE: Record<string, CourierBatchField[]> = {
  [CourierBatchDiagnosticCode.NoSignIn]: ['mobile', 'externalID', 'username', 'accessCode'],
  [CourierBatchDiagnosticCode.Ambiguous]: ['firstName']
};

/** The codes that point at the carrier picker, whatever field their diagnostic names. */
const CARRIER_CODES: string[] = [
  CourierBatchDiagnosticCode.CarrierMissing,
  CourierBatchDiagnosticCode.CarrierForbidden,
  CourierBatchDiagnosticCode.CarrierUnavailable,
  CourierBatchDiagnosticCode.CarrierUnknown,
  CourierBatchDiagnosticCode.CarrierAmbiguous,
  CourierBatchDiagnosticCode.WrongCarrier
];

/** The diagnostics that point at one cell, by the field they name or by their code, for its highlight and tooltip. */
export function diagnosticsForCell(row: CourierBatchRow, field: CourierBatchField): BatchDiagnostic[] {
  return row.diagnostics.filter(
    (diagnostic) => diagnostic.field === field || !!CELLS_BY_CODE[diagnostic.code]?.includes(field)
  );
}

/**
 * The diagnostics the carrier picker answers.
 */
export function diagnosticsForCarrierCell(row: CourierBatchRow): BatchDiagnostic[] {
  return row.diagnostics.filter(
    (diagnostic) =>
      diagnostic.field === 'groupName' || diagnostic.field === 'groupID' || CARRIER_CODES.includes(diagnostic.code)
  );
}

/** The worst severity among some diagnostics, or nothing when there are none. */
export function worstSeverity(diagnostics: BatchDiagnostic[]): CourierBatchDiagnosticSeverity | undefined {
  if (diagnostics.some((diagnostic) => diagnostic.severity === KnownBatchDiagnosticSeverity.Error)) {
    return 'error';
  }
  return diagnostics.length > 0 ? 'warning' : undefined;
}

export function editRowValue(row: CourierBatchRow, field: CourierBatchField, value: string): CourierBatchRow {
  return withValues(row, { ...row.values, [field]: value });
}

/**
 * A pick in the carrier picker.
 */
export function setRowCarrier(row: CourierBatchRow, unit: CarrierUnitDto | undefined): CourierBatchRow {
  const { groupID: _groupID, groupName: _groupName, ...values } = row.values;
  const next = withValues(row, unit ? { ...values, groupID: unit.id, groupName: unit.name } : values);
  // Where the last prepare routed the old carrier no longer says anything about the new one.
  return next === row ? row : { ...next, resolvedCarrier: undefined };
}

/** The diagnostics the API gives a carrier the file names but that it can't route to. */
export const INVALID_CARRIER_CODES: string[] = [
  CourierBatchDiagnosticCode.CarrierUnknown,
  CourierBatchDiagnosticCode.CarrierAmbiguous
];

/** Whether the API rejected the carrier a row names, as unknown or ambiguous. */
export function hasRejectedCarrier(row: CourierBatchRow): boolean {
  return row.diagnostics.some((diagnostic) => INVALID_CARRIER_CODES.includes(diagnostic.code));
}

export function clearUnmatchedCarriers(rows: CourierBatchRow[]): { rows: CourierBatchRow[]; cleared: number } {
  let cleared = 0;
  const next = rows.map((row) => {
    if (isRowLocked(row) || !hasRejectedCarrier(row) || !hasOwnCarrier(row.values)) {
      return row;
    }
    cleared++;
    // Not `setRowCarrier`: that's an operator's edit, which would take the row out of error.
    const { groupID: _groupID, groupName: _groupName, ...values } = row.values;
    return { ...row, values, resolvedCarrier: undefined };
  });

  return { rows: next, cleared };
}

export function resolveCarrierPickerValue(
  values: CourierBatchValues,
  units: CarrierUnitDto[],
  resolvedCarrier?: CourierBatchResolvedCarrier
): { unit?: CarrierUnitDto; isDefault: boolean } {
  if (resolvedCarrier) {
    return { unit: unitOrLabel(units, resolvedCarrier.id, resolvedCarrier.name), isDefault: !hasOwnCarrier(values) };
  }
  const groupID = values.groupID?.trim();
  const groupName = values.groupName?.trim();
  return { unit: groupID || groupName ? unitOrLabel(units, groupID, groupName) : undefined, isDefault: false };
}

/** The unit with this ID, or one to display by name when it isn't among the units. */
function unitOrLabel(units: CarrierUnitDto[], id: string | undefined, name: string | undefined): CarrierUnitDto {
  return (
    units.find((candidate) => !!id && candidate.id === id) ??
    ({ id: id ?? '', name: name || id || '' } as CarrierUnitDto)
  );
}

function hasOwnCarrier(values: CourierBatchValues): boolean {
  return !!values.groupID?.trim() || !!values.groupName?.trim();
}

/** The rows in the import request wait on it with a spinner. */
export function markRowsUploading(rows: CourierBatchRow[], rowIDs: string[]): CourierBatchRow[] {
  const sending = new Set(rowIDs);
  return rows.map((row) => (sending.has(row.rowID) ? { ...row, status: 'uploading' } : row));
}

/** What an upload sends: the ticked rows, of those that can be ticked. */
export function selectUploadableRowIDs(rows: CourierBatchRow[]): string[] {
  return rows.filter((row) => isRowSelectable(row) && row.isSelected).map((row) => row.rowID);
}

/**
 * Whether a row's checkbox can be ticked. Rows with errors can't be — they aren't uploaded, as the
 * operator is told — nor can rows in flight or already imported.
 */
export function isRowSelectable(row: CourierBatchRow): boolean {
  return row.status !== 'error' && !isRowLocked(row);
}

/**
 * Whether a row takes the error tint: it failed validation, or it was edited since and not validated
 * again, so it still shows the errors the last validate found.
 */
export function hasRowErrorTint(row: CourierBatchRow): boolean {
  return row.status === 'error' || (row.status === 'edited' && worstSeverity(row.diagnostics) === 'error');
}

/** Ticks or unticks one row, when it can be ticked. */
export function toggleRowSelected(row: CourierBatchRow): CourierBatchRow {
  return isRowSelectable(row) ? { ...row, isSelected: !row.isSelected } : row;
}

/** The header's select all: ticks or unticks every row that can be ticked, and leaves the rest. */
export function setAllRowsSelected(rows: CourierBatchRow[], isSelected: boolean): CourierBatchRow[] {
  return rows.map((row) => (row.isSelected !== isSelected ? toggleRowSelected(row) : row));
}

/** What the header checkbox shows: all, some, or none of the rows that can be ticked. */
export function summariseSelection(rows: CourierBatchRow[]): { selectable: number; selected: number } {
  const selectable = rows.filter(isRowSelectable);
  return { selectable: selectable.length, selected: selectable.filter((row) => row.isSelected).length };
}

/** Every courier is in: nothing is left to validate or upload. */
export function isBatchComplete(rows: CourierBatchRow[]): boolean {
  return rows.length > 0 && rows.every((row) => row.status === 'uploaded');
}

export function isRowLocked(row: CourierBatchRow): boolean {
  return row.status === 'uploading' || row.status === 'uploaded';
}

function withValues(row: CourierBatchRow, values: CourierBatchValues): CourierBatchRow {
  if (isRowLocked(row)) {
    return row;
  }
  return { ...row, values, status: 'edited', isSelected: row.status === 'error' ? true : row.isSelected };
}
